import { getAuthProvider } from '@/shared/lib/auth/auth-config'
import { coreUrl } from '@/shared/lib/api/api-base'
import { ACCESS_SUSPENDED_CODE, notifyAccessSuspended } from '@/shared/lib/auth/access-suspended'

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

interface RequestOptions {
  method?: HttpMethod
  body?: unknown
  signal?: AbortSignal
}

export class ApiError extends Error {
  // `details` carries the raw JSON body of a 4xx/5xx response when it could
  // be parsed (e.g. `{ ExistingFileUuid, Field, Message, ... }`). Optional —
  // existing call sites that pass only three args continue to work. Callers
  // that need to surface structured error payloads (e.g. 409 conflict
  // metadata) can read `error.details` instead of re-parsing the response.
  public readonly details?: Record<string, unknown>

  constructor(
    public readonly status: number,
    public readonly errors: string[],
    message: string,
    details?: Record<string, unknown>,
  ) {
    super(message)
    this.name = 'ApiError'
    this.details = details
  }
}

async function requestBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
  const { method = 'GET', body, signal } = options

  const authProvider = getAuthProvider()
  const token = await authProvider.getToken()

  const headers: HeadersInit = { 'Content-Type': 'application/json' }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const response = await fetch(coreUrl(path), {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  })

  if (response.status === 401) {
    await authProvider.logout()
    throw new ApiError(401, ['Unauthorized'], 'Session expired')
  }
  if (!response.ok) {
    throw new ApiError(response.status, [], `Request failed: ${response.status}`)
  }

  return response.blob()
}

async function requestText(path: string, body?: unknown, signal?: AbortSignal): Promise<string> {
  const authProvider = getAuthProvider()
  const token = await authProvider.getToken()

  const headers: HeadersInit = { 'Content-Type': 'application/json' }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const response = await fetch(coreUrl(path), {
    method: 'POST',
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  })

  if (response.status === 401) {
    await authProvider.logout()
    throw new ApiError(401, ['Unauthorized'], 'Session expired')
  }
  if (!response.ok) {
    throw new ApiError(response.status, [], `Request failed: ${response.status}`)
  }

  return response.text()
}

async function requestFormData<T>(path: string, formData: FormData, signal?: AbortSignal): Promise<T> {
  const authProvider = getAuthProvider()
  const token = await authProvider.getToken()

  const headers: HeadersInit = {}
  if (token) headers['Authorization'] = `Bearer ${token}`

  const response = await fetch(coreUrl(path), {
    method: 'POST',
    headers,
    body: formData,
    signal,
  })

  if (response.status === 401) {
    await authProvider.logout()
    throw new ApiError(401, ['Unauthorized'], 'Session expired')
  }
  if (!response.ok) {
    throw new ApiError(response.status, [], `Request failed: ${response.status}`)
  }
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T
  }
  return response.json() as Promise<T>
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal } = options

  const authProvider = getAuthProvider()
  const token = await authProvider.getToken()

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(coreUrl(path), {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  })

  if (response.status === 401) {
    await authProvider.logout()
    throw new ApiError(401, ['Unauthorized'], 'Session expired')
  }

  if (response.status === 403) {
    // Some endpoints explain the refusal with `{ errorCode, message }` (e.g. account delete,
    // th_saas CU-90). The body rides along in `details`; status, errors and message stay as they were.
    let forbiddenDetails: Record<string, unknown> | undefined
    try {
      const parsedBody = await response.json() as unknown
      if (typeof parsedBody === 'object' && parsedBody !== null && !Array.isArray(parsedBody)) {
        forbiddenDetails = parsedBody as Record<string, unknown>
      }
    } catch {
      // ignore parse error
    }
    // A suspended or deactivated membership in this tenant (UC-084.3 E2): never a logout.
    if ((forbiddenDetails?.Code ?? forbiddenDetails?.code) === ACCESS_SUSPENDED_CODE) notifyAccessSuspended()
    throw new ApiError(403, ['Forbidden'], 'Insufficient permissions', forbiddenDetails)
  }

  if (!response.ok) {
    let errors: string[] = []
    // The whole body travels along in `details`: `errors` is the flat message list every endpoint
    // returns, but some of them put a structured payload next to it — per-field validation errors,
    // conflict metadata — that only the caller knows how to read. Objects only, so `details` keeps
    // its `Record` shape; a body that is an array or a bare scalar carries nothing addressable.
    let details: Record<string, unknown> | undefined
    try {
      const parsedBody = await response.json() as unknown
      if (typeof parsedBody === 'object' && parsedBody !== null && !Array.isArray(parsedBody)) {
        details = parsedBody as Record<string, unknown>
        // Declared as a string list because that is what the endpoints return, but handed over
        // untouched: ASP.NET model validation puts a field-to-messages map under the same key, and
        // the callers that care flatten it themselves.
        errors = (details.errors as string[] | undefined) ?? []
      }
    } catch {
      // ignore parse error
    }
    throw new ApiError(
      response.status,
      errors,
      errors[0] ?? `Request failed: ${response.status}`,
      details,
    )
  }

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T
  }

  const contentType = response.headers.get('content-type') ?? ''
  if (
    contentType.includes('application/octet-stream') ||
    contentType.includes('text/csv') ||
    contentType.includes('application/vnd')
  ) {
    return response.blob() as Promise<T>
  }

  return response.json() as Promise<T>
}

export const apiClient = {
  get: <T>(path: string, signal?: AbortSignal) =>
    request<T>(path, { method: 'GET', signal }),

  post: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'POST', body, signal }),

  postBlob: (path: string, body?: unknown, signal?: AbortSignal) =>
    requestBlob(path, { method: 'POST', body, signal }),

  getBlob: (path: string, signal?: AbortSignal) =>
    requestBlob(path, { method: 'GET', signal }),

  put: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'PUT', body, signal }),

  patch: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'PATCH', body, signal }),

  delete: <T>(path: string, signal?: AbortSignal) =>
    request<T>(path, { method: 'DELETE', signal }),

  postForm: <T>(path: string, formData: FormData, signal?: AbortSignal) =>
    requestFormData<T>(path, formData, signal),

  postText: (path: string, body?: unknown, signal?: AbortSignal) =>
    requestText(path, body, signal),
}